import React, { useMemo } from "react";
import styled from "@emotion/styled";
import { DateTime, Interval } from "luxon";
import { Bar, BarChart, XAxis, YAxis } from "recharts";
import { Flex } from "styles/utility-components";

import { currencyFormat, numberFormatLong } from "components/app_layout/Typography/Numbers";
import { convertNoticeFiltersToSearchRequest, NoticeFilters } from "components/notices/utils";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "components/ui/chart/Chart";
import { dateSort, numberSort } from "lib/columnSort";
import { Table } from "lib/core_components/Table";
import { useNoticesAnalysisByPeriod } from "lib/hooks/api/notices/analysis/useNoticesAnalysisByPeriod";
import {
  NoticeAnalysisByPeriodGroupBy,
  NoticeAnalysisByPeriodInterval,
} from "lib/types/graphQLEnums";
import EmptyAnalysis from "./EmptyAnalysis";
import HeadlineFigure from "./HeadlineFigure";

const intervalEnumToLabel = (enumValue: NoticeAnalysisByPeriodInterval) => {
  switch (enumValue) {
    case NoticeAnalysisByPeriodInterval.Month:
      return "Month";
    case NoticeAnalysisByPeriodInterval.Quarter:
      return "Quarter";
    case NoticeAnalysisByPeriodInterval.Year:
      return "Year";
  }
};

const dateRangeToString = (
  interval: NoticeAnalysisByPeriodInterval,
  dateStart: string,
  short: boolean = false,
) => {
  const startDate = new Date(dateStart);

  if (interval === NoticeAnalysisByPeriodInterval.Month) {
    return `${startDate.toLocaleString("default", { month: short ? "short" : "long" })} ${startDate.getFullYear()}`;
  } else if (interval === NoticeAnalysisByPeriodInterval.Quarter) {
    return `Q${Math.ceil((startDate.getMonth() + 1) / 3)} ${startDate.getFullYear()}`;
  } else {
    return `${startDate.getFullYear()}`;
  }
};

type ByPeriodProps = {
  filters: NoticeFilters;
  groupBy: NoticeAnalysisByPeriodGroupBy;
  interval: NoticeAnalysisByPeriodInterval;
  chartShow: "noticeCount" | "noticeValues";
  setFilters: (newValue: React.SetStateAction<NoticeFilters | undefined>) => void;
  selectedViewId?: string;
};

function ByPeriod({
  filters,
  groupBy,
  interval,
  chartShow,
  setFilters,
  selectedViewId,
}: ByPeriodProps) {
  const { data, isLoading, isError } = useNoticesAnalysisByPeriod(
    convertNoticeFiltersToSearchRequest(filters),
    {
      groupBy,
      interval,
    },
  );

  const chartData = useMemo(() => {
    const results = data?.analysis?.byPeriod?.results;
    if (!results || results.length === 0) {
      return [];
    }

    const mappedData = results
      .map((result) => ({
        ...result,
        date: dateRangeToString(interval, result.dateStart),
        dateStart: DateTime.fromISO(result.dateStart, { zone: "utc" }),
        dateEnd: DateTime.fromISO(result.dateEnd, { zone: "utc" }),
      }))
      .sort((a, b) => {
        if (a.dateStart < b.dateStart) {
          return -1;
        }
        if (a.dateStart > b.dateStart) {
          return 1;
        }
        return 0;
      });

    // Need to fill in gaps which have no data
    const startDate = mappedData[0]?.dateStart;
    const endDate = mappedData[mappedData.length - 1]?.dateEnd;
    if (!startDate || !endDate) {
      throw new Error(`Start date "${startDate}" or end date "${endDate}" is null/undefined`);
    }
    const dateRange = Interval.fromDateTimes(startDate, endDate);
    const intervals = dateRange.splitBy({ [interval]: 1 });

    let mappedDataCounter = 0;
    const filledData = intervals.map((split) => {
      const foundData = mappedData[mappedDataCounter];
      if (split.start === null) {
        throw new Error("Split start is null");
      }
      if (foundData) {
        if (split.start.equals(foundData.dateStart)) {
          mappedDataCounter++;
          return foundData;
        }
        if (split.start > foundData.dateStart) {
          throw new Error("Found data is older than split start");
        }
      }
      return {
        date: dateRangeToString(interval, split.start.toISO() ?? ""),
        dateStart: split.start,
        dateEnd: split.end,
        noticeCount: 0,
        noticeValues: 0,
      };
    });

    return filledData;
  }, [data?.analysis?.byPeriod?.results, interval]);

  if (data?.analysis?.byPeriod?.results.length === 0) {
    return (
      <EmptyAnalysis filters={filters} setFilters={setFilters} selectedViewId={selectedViewId} />
    );
  }

  return (
    <Wrapper column flexGrow={1} gap={24}>
      <Flex gap={24}>
        <HeadlineFigure
          label="Notice"
          value={data?.totalResults}
          valueType="number"
          isLoading={isLoading}
        />
        <HeadlineFigure
          label="Total Value"
          value={data?.analysis?.overall?.noticeValues}
          valueType="currency"
          isLoading={isLoading}
          noPluralise
        />
      </Flex>
      <StyledChartContainer
        config={{
          date: {
            label: "Date",
          },
          noticeCount: {
            label: "Notice count",
            color: "sysChart1",
            tooltipFormatter: numberFormatLong,
          },
          noticeValues: {
            label: "Notice value",
            color: "sysChart1",
            tooltipFormatter: (value) => currencyFormat(value, "GBP"),
          },
        }}
      >
        <BarChart accessibilityLayer data={chartData}>
          <XAxis dataKey="date" tickLine={false} axisLine={false} tickMargin={8} minTickGap={32} />
          <YAxis hide domain={[0, "dataMax"]} dataKey={chartShow} />
          <ChartTooltip
            content={
              <ChartTooltipContent
                hideIndicator
                extraRows={[chartShow === "noticeValues" ? "noticeCount" : "noticeValues"]}
              />
            }
          />
          <Bar dataKey={chartShow} fill={`var(--color-${chartShow})`} radius={4} />
        </BarChart>
      </StyledChartContainer>
      <Table
        dataSource={chartData}
        rowKey="date"
        isError={isError}
        loading={isLoading}
        columns={[
          {
            key: "dateRange",
            dataIndex: "dateStart",
            title: intervalEnumToLabel(interval),
            render: (_: unknown, record) => <div>{record.date}</div>,
            sorter: dateSort((r) => r.dateStart),
          },
          {
            key: "noticeCount",
            dataIndex: "noticeCount",
            title: "Notice count",
            render: (_: unknown, record) => <div>{numberFormatLong(record.noticeCount)}</div>,
            sorter: numberSort((r) => r.noticeCount),
          },
          {
            key: "noticeValue",
            dataIndex: "noticeValues",
            title: "Notice value",
            render: (_: unknown, record) => <div>{currencyFormat(record.noticeValues, "GBP")}</div>,
            sorter: numberSort((r) => r.noticeValues),
          },
        ]}
      />
    </Wrapper>
  );
}

const Wrapper = styled(Flex)({
  maxWidth: "100%",
});

const StyledChartContainer = styled(ChartContainer)({
  maxHeight: "max(400px, 60vh)",
  svg: {
    overflow: "visible", // Fix for X-Axis tick label slightly clipping. Potentially move into chart file itself if it comes up again.
  },
});

export default ByPeriod;
